/**
 * AdaSupplyBreakdown — V2.K.7
 *
 * Renders a supply breakdown for ADA on the CardanoMonitor page.
 *
 * Why Cardano is its own story :
 *  - **Hard cap 45 B ADA**, fixed at genesis. No inflation beyond
 *    this cap.
 *  - Monetary policy is **exponential reserve depletion** : each
 *    5-day epoch releases ρ = 0.3 % of the remaining reserves. This
 *    gives a smooth exponential decay with a half-life of ~4-5 years
 *    — no halvings, no cliffs.
 *  - τ = 20 % of the per-epoch expansion goes to the on-chain
 *    treasury. The treasury is governed under Voltaire / Chang
 *    hard fork via DRep + SPO + Constitutional Committee votes.
 *  - Net Change Limit (2025) capped treasury withdrawals at 350 M
 *    ADA — a guardrail against governance attacks.
 *  - >60 % of circulating ADA is staked across 3000+ stake pools.
 *    Staking has no slashing and no lock-up.
 */

import { useTranslation } from 'react-i18next';
import { useAdaSupply } from '../../hooks/useAdaSupply';

// Net Change Limit 2025 — treasury withdrawal cap.
const ADA_NCL_2025 = 350_000_000;

const KNOWN_ENDPOINTS: Array<{
  label: string;
  url: string;
  category: 'docs' | 'foundation' | 'analysis';
  note: string;
}> = [
  {
    label: 'Cardano Monetary Policy (docs officielles)',
    url: 'https://docs.cardano.org/about-cardano/explore-more/monetary-policy',
    category: 'docs',
    note: 'ρ = 0,3 %/epoch, τ = 20 %, half-life ~4-5 ans.',
  },
  {
    label: 'Understanding Cardano\'s Net Change Limit',
    url: 'https://cardanofoundation.org/blog/understanding-cardanos-net-change-limit',
    category: 'foundation',
    note: 'Cap 350 M ADA sur les withdrawals treasury (Net Change Limit 2025).',
  },
  {
    label: 'State of Cardano Q4 2025 — Messari',
    url: 'https://messari.io/report/state-of-cardano-q4-2025',
    category: 'analysis',
    note: 'Rapport trimestriel : supply, treasury, gouvernance Voltaire.',
  },
  {
    label: 'Intersect MBO — Governance Actions',
    url: 'https://www.intersectmbo.org/news/recent-cardano-governance-actions',
    category: 'foundation',
    note: 'Suivi des actions gouvernance Chang / Voltaire (DRep + SPO + CC).',
  },
];

export function AdaSupplyBreakdown() {
  const { t, i18n } = useTranslation();
  const { data, isLoading, error } = useAdaSupply();

  const locale = i18n?.language || 'fr';
  const fmt = (n: number) =>
    n.toLocaleString(locale === 'fr' ? 'fr-FR' : locale, { maximumFractionDigits: 0 });
  const fmtB = (n: number) =>
    `${(n / 1_000_000_000).toLocaleString(locale === 'fr' ? 'fr-FR' : locale, {
      maximumFractionDigits: 2,
    })} Md`;

  return (
    <section
      aria-labelledby="ada-supply-breakdown-title"
      style={{
        background: 'rgba(0, 0, 0, 0.25)',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        padding: '20px',
        marginBottom: '20px',
      }}
    >
      <header style={{ marginBottom: '14px' }}>
        <h2
          id="ada-supply-breakdown-title"
          style={{
            fontSize: '16px',
            fontWeight: 700,
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span aria-hidden style={{ color: '#0033AD' }}>◆</span>
          {t('ada.supply.title', 'Supply ADA — répartition')}
        </h2>
        <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
          {t(
            'ada.supply.subtitle',
            'Cap fixe 45 Md. Reserve depletion exponentielle (ρ = 0,3 %/epoch, half-life ~4-5 ans). Treasury on-chain reçoit τ = 20 % de l\'expansion, gouverné via Voltaire / Chang.',
          )}
        </div>
      </header>

      {isLoading ? (
        <SkeletonBlock />
      ) : error || !data ? (
        <ErrorBlock />
      ) : (
        <>
          <StatGrid data={data} fmt={fmt} fmtB={fmtB} t={t} />
          <StackedBar data={data} t={t} />
          <Legend t={t} />
          <PolicyNote t={t} fmt={fmt} fmtB={fmtB} data={data} ncl={ADA_NCL_2025} />
          <KnownEndpoints t={t} />
        </>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Stat grid
// ─────────────────────────────────────────────────────────────────────

interface StatGridProps {
  data: NonNullable<ReturnType<typeof useAdaSupply>['data']>;
  fmt: (n: number) => string;
  fmtB: (n: number) => string;
  t: (key: string, fallback: string) => string;
}

function StatGrid({ data, fmt, fmtB, t }: StatGridProps) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '12px',
        marginBottom: '16px',
      }}
    >
      <StatCard
        label={t('ada.supply.circulating', 'En circulation')}
        value={fmtB(data.circulating)}
        sub={`${fmt(data.circulating)} ADA`}
        color="#0033AD"
      />
      <StatCard
        label={t('ada.supply.reserve', 'Reserve restante')}
        value={fmtB(data.reserveRemaining)}
        sub={`~${fmtB(data.annualReserveRelease)} libérés / an au taux actuel`}
        color="#f59e0b"
      />
      <StatCard
        label={t('ada.supply.treasury', 'Treasury on-chain')}
        value={fmtB(data.treasuryReference)}
        sub={`+${fmtB(data.annualTreasuryFlow)} / an (τ = 20 %)`}
        color="#10b981"
      />
      <StatCard
        label={t('ada.supply.max', 'Cap maximal')}
        value={fmtB(data.max)}
        sub={`${data.mintedPct.toFixed(2)} % déjà mintés`}
        color="#6366f1"
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string;
  sub: string;
  color: string;
}) {
  return (
    <div
      style={{
        background: `${color}10`,
        border: `1px solid ${color}30`,
        borderRadius: '10px',
        padding: '12px 14px',
      }}
    >
      <div
        style={{
          fontSize: '11px',
          color: 'var(--muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.4px',
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: '20px',
          fontWeight: 700,
          color: 'var(--text)',
          marginTop: '4px',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: '11px',
          color: 'var(--muted)',
          marginTop: '2px',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {sub}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Stacked bar : circulating / treasury / reserve-still-to-release
// ─────────────────────────────────────────────────────────────────────

function StackedBar({
  data,
  t,
}: {
  data: NonNullable<ReturnType<typeof useAdaSupply>['data']>;
  t: (key: string, fallback: string) => string;
}) {
  const circulatingPct = (data.circulating / data.max) * 100;
  const treasuryPct = (data.treasuryReference / data.max) * 100;
  const reservePct = Math.max(0, 100 - circulatingPct - treasuryPct);

  return (
    <div
      style={{ marginBottom: '8px' }}
      aria-label={t(
        'ada.supply.barAlt',
        'Répartition de la supply ADA sur le cap fixe de 45 milliards : circulating, treasury on-chain, reserve restante',
      )}
      role="img"
    >
      <div
        style={{
          display: 'flex',
          height: '24px',
          borderRadius: '8px',
          overflow: 'hidden',
          border: '1px solid var(--border)',
        }}
      >
        <div
          style={{
            width: `${circulatingPct}%`,
            background: 'linear-gradient(90deg, #0033AD, #1e5fcc)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '11px',
            fontWeight: 700,
            transition: 'width 0.5s ease',
          }}
          title={`${circulatingPct.toFixed(1)} %`}
        >
          {circulatingPct > 8 ? `${circulatingPct.toFixed(0)} %` : ''}
        </div>
        <div
          style={{
            width: `${treasuryPct}%`,
            background: 'linear-gradient(90deg, #10b981, #34d399)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '11px',
            fontWeight: 700,
            transition: 'width 0.5s ease',
          }}
          title={`${treasuryPct.toFixed(1)} %`}
        >
          {treasuryPct > 2 ? `${treasuryPct.toFixed(1)} %` : ''}
        </div>
        <div
          style={{
            width: `${reservePct}%`,
            background:
              'repeating-linear-gradient(45deg, rgba(245, 158, 11, 0.10), rgba(245, 158, 11, 0.10) 6px, rgba(245, 158, 11, 0.20) 6px, rgba(245, 158, 11, 0.20) 12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fbbf24',
            fontSize: '10px',
            fontWeight: 600,
            transition: 'width 0.5s ease',
          }}
          title={`${reservePct.toFixed(1)} %`}
        >
          {reservePct > 8 ? `${reservePct.toFixed(1)} %` : ''}
        </div>
      </div>
    </div>
  );
}

function Legend({ t }: { t: (key: string, fallback: string) => string }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: '14px',
        flexWrap: 'wrap',
        fontSize: '11px',
        color: 'var(--muted)',
        marginBottom: '14px',
      }}
    >
      <LegendItem color="#0033AD" label={t('ada.supply.legendCirc', 'En circulation')} />
      <LegendItem color="#10b981" label={t('ada.supply.legendTreasury', 'Treasury on-chain (τ = 20 %)')} />
      <LegendItem color="#f59e0b" label={t('ada.supply.legendReserve', 'Reserve — décroissance exponentielle (ρ = 0,3 %/epoch)')} />
    </div>
  );
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      <span
        aria-hidden
        style={{
          width: '10px',
          height: '10px',
          borderRadius: '3px',
          background: color,
          display: 'inline-block',
        }}
      />
      {label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Monetary policy callout
// ─────────────────────────────────────────────────────────────────────

function PolicyNote({
  t,
  fmt,
  fmtB,
  data,
  ncl,
}: {
  t: (key: string, fallback: string) => string;
  fmt: (n: number) => string;
  fmtB: (n: number) => string;
  data: NonNullable<ReturnType<typeof useAdaSupply>['data']>;
  ncl: number;
}) {
  const body = t(
    'ada.supply.policyBody',
    "Chaque epoch (5 jours), 0,3 % des reserves restantes sont libérés — 80 % vers les rewards SPO/délégateurs, 20 % vers le treasury. À ce taux, ~{{annual}} ADA seront libérés cette année (le rythme ralentit naturellement à mesure que la reserve diminue, half-life ~4-5 ans). Le treasury reçoit ~{{treasuryAnnual}} / an et s'élève actuellement à ~{{treasury}}. Précision technique importante : le treasury Cardano n'est PAS une adresse au sens classique — c'est l'un des « six pots » de comptabilité du ledger (réserves + treasury parmi eux), géré au niveau protocole et non comme un UTxO avec clés (confirmé par ingénieur IOHK). Depuis le hard fork Chang et l'ère Voltaire (2024-2025), les withdrawals treasury sont votés par les DReps + SPOs + Constitutional Committee, plafonnés par le Net Change Limit 2025 à {{ncl}} ADA.",
  )
    .replace('{{annual}}', fmtB(data.annualReserveRelease))
    .replace('{{treasuryAnnual}}', fmtB(data.annualTreasuryFlow))
    .replace('{{treasury}}', fmtB(data.treasuryReference))
    .replace('{{ncl}}', fmt(ncl));

  return (
    <div
      style={{
        padding: '12px 14px',
        background: 'rgba(0, 51, 173, 0.06)',
        border: '1px dashed rgba(0, 51, 173, 0.35)',
        borderRadius: '10px',
        fontSize: '12px',
        color: 'var(--text)',
        lineHeight: 1.6,
        marginBottom: '14px',
      }}
    >
      <strong style={{ color: '#3b82f6' }}>
        {t('ada.supply.policyTitle', 'Monetary policy Ouroboros + treasury Voltaire')}
      </strong>
      <div style={{ marginTop: '4px' }}>{body}</div>
      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '6px' }}>
        {t(
          'ada.supply.policySource',
          'Sources : docs.cardano.org/monetary-policy, cardanofoundation.org/net-change-limit, Messari State of Cardano Q4 2025.',
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Known endpoints
// ─────────────────────────────────────────────────────────────────────

function KnownEndpoints({ t }: { t: (key: string, fallback: string) => string }) {
  return (
    <details
      style={{
        background: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid var(--border)',
        borderRadius: '10px',
        padding: '12px 14px',
      }}
    >
      <summary
        style={{
          fontSize: '13px',
          fontWeight: 600,
          color: 'var(--text)',
          cursor: 'pointer',
          listStyle: 'revert',
        }}
      >
        {t('ada.supply.knownEndpointsTitle', 'Sources officielles & gouvernance')}
      </summary>

      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '8px', lineHeight: 1.6 }}>
        {t(
          'ada.supply.knownEndpointsDisclaimer',
          'Cardano n\'a pas de burn dans sa politique monétaire. Reserve et treasury sont gérés au niveau du ledger comme des « pots » comptables (pas comme des UTxOs avec clés). Côté genesis : seule l\'allocation Cardano Foundation est confirmée en source primaire à 648 176 761 ADA. Les pourcentages relatifs IOHK / EMURGO / Foundation et le total agrégé qui circulent dans la communauté n\'ont pas été retrouvés dans une source officielle vérifiable — pour les chiffres exacts, parcourir byron-genesis.json du repo cardano-node (sets d\'adresses, pas wallets uniques). Utiliser cardanoscan.io ou les rapports Messari pour des chiffres en live.',
        )}
      </div>

      <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0 0' }}>
        {KNOWN_ENDPOINTS.map((entry) => (
          <li
            key={entry.url}
            style={{
              padding: '10px 0',
              borderTop: '1px solid var(--border)',
              fontSize: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
              <CategoryPill category={entry.category} />
              <a
                href={entry.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--text)', fontWeight: 600, textDecoration: 'none' }}
              >
                {entry.label} ↗
              </a>
            </div>
            <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
              {entry.note}
            </div>
          </li>
        ))}
      </ul>
    </details>
  );
}

function CategoryPill({ category }: { category: 'docs' | 'foundation' | 'analysis' }) {
  const styles: Record<typeof category, { bg: string; color: string; label: string }> = {
    docs: { bg: 'rgba(0, 51, 173, 0.20)', color: '#7da6f0', label: 'Docs' },
    foundation: { bg: 'rgba(99, 102, 241, 0.15)', color: '#818cf8', label: 'Foundation' },
    analysis: { bg: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24', label: 'Analyse' },
  };
  const s = styles[category];
  return (
    <span
      style={{
        fontSize: '10px',
        fontWeight: 700,
        padding: '2px 6px',
        borderRadius: '4px',
        background: s.bg,
        color: s.color,
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
      }}
    >
      {s.label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Loading / error states
// ─────────────────────────────────────────────────────────────────────

function SkeletonBlock() {
  return (
    <div aria-hidden>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px',
          marginBottom: '16px',
        }}
      >
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            style={{
              background: 'rgba(255, 255, 255, 0.04)',
              borderRadius: '10px',
              height: '72px',
            }}
          />
        ))}
      </div>
      <div
        style={{
          height: '24px',
          background: 'rgba(255, 255, 255, 0.04)',
          borderRadius: '8px',
          marginBottom: '14px',
        }}
      />
    </div>
  );
}

function ErrorBlock() {
  return (
    <div
      style={{
        padding: '14px',
        background: 'rgba(239, 68, 68, 0.08)',
        border: '1px solid rgba(239, 68, 68, 0.25)',
        borderRadius: '10px',
        fontSize: '12px',
        color: 'var(--muted)',
      }}
    >
      Données CoinGecko indisponibles pour le moment. La supply ADA sera affichée dès que l'API répondra.
    </div>
  );
}
